import React from 'react';
import { motion } from 'framer-motion';
import { Check, Sparkles, ArrowRight, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';

const PricingSection = () => {
  const { t, language } = useLanguage();

  const invoiceFeatures = t('pricing_invoice_features') || [];
  const posFeatures = t('pricing_pos_features') || [];

  // Yearly prices with 15% off
  const invoiceYearly = (4.99 * 12 * 0.85).toFixed(2);   // 50.90
  const invoiceOfferYearly = (invoiceYearly * 0.50).toFixed(2); // 25.45
  const posYearly = (19.99 * 12 * 0.85).toFixed(2);       // 203.90
  const posOfferYearly = (posYearly * 0.50).toFixed(2);   // 101.95

  return (
    <section id="pricing" className="vf-section bg-white" data-testid="pricing-section">
      <div className="vf-container">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-center mb-16">
          <h2 className="vf-heading-2 text-[#1e3a5f] mb-4">{t('pricing_title')}</h2>
          <p className="vf-body-large text-slate-600 max-w-2xl mx-auto">{t('pricing_subtitle')}</p>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-3 mt-6 px-6 py-3 bg-gradient-to-r from-[#1e3a5f] to-[#2d5a8f] text-white font-bold rounded-sm shadow-lg">
            <Clock className="w-5 h-5 text-yellow-300 flex-shrink-0" />
            <span className="text-sm sm:text-base">{t('pricing_launch')}</span>
            <Sparkles className="w-5 h-5 text-yellow-300 flex-shrink-0" />
          </motion.div>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Invoice Plan */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.5 }} className="vf-card-pricing" data-testid="pricing-invoice-card">
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-[#1e3a5f]">{t('pricing_invoice_title')}</h3>
                <p className="text-slate-500">{t('pricing_invoice_subtitle')}</p>
              </div>

              {/* Monthly Price */}
              <div className="flex items-baseline gap-1">
                <span className="text-5xl font-bold text-[#1e3a5f]">€4.99</span>
                <span className="text-xl text-slate-500">{t('pricing_per_month')}</span>
              </div>

              {/* Yearly price with 15% off */}
              <div className="bg-slate-50 border-2 border-slate-200 px-4 py-3">
                <p className="text-sm font-semibold text-[#1e3a5f] mb-1">{t('pricing_yearly')} ({t('pricing_yearly_save')})</p>
                <span className="text-[#1e3a5f] font-bold text-lg">€{invoiceYearly}{t('pricing_per_year')}</span>
                <span className="text-slate-400 text-sm ml-2">€{(invoiceYearly / 12).toFixed(2)}{t('pricing_per_month')}</span>
              </div>

              {/* 50% off yearly offer */}
              <div className="bg-amber-50 border-2 border-amber-300 px-4 py-3">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                  <span className="text-amber-800 font-bold text-sm">50% OFF {t('pricing_yearly')}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="line-through text-slate-400 text-sm">€{invoiceYearly}</span>
                  <span className="text-[#22c55e] font-bold text-xl">€{invoiceOfferYearly}{t('pricing_per_year')}</span>
                </div>
                <p className="text-xs text-amber-700 mt-1">€{(invoiceOfferYearly / 12).toFixed(2)}{t('pricing_per_month')} · {t('pricing_billed_yearly')}</p>
              </div>

              <ul className="space-y-3">
                {Array.isArray(invoiceFeatures) && invoiceFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#22c55e] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link to="/register?plan=invoice"
                className="vf-btn-secondary w-full flex items-center justify-center gap-2"
                data-testid="pricing-invoice-button">
                {t('pricing_cta_invoice')} <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>

          {/* POS Plan */}
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="vf-card-pricing border-[#22c55e] scale-[1.02] relative overflow-hidden"
            data-testid="pricing-pos-card">
            <div className="absolute top-0 right-0 bg-[#22c55e] text-white px-6 py-2 text-sm font-bold">
              {t('pricing_popular')}
            </div>
            <div className="space-y-6 pt-4">
              <div>
                <h3 className="text-2xl font-bold text-[#1e3a5f]">{t('pricing_pos_title')}</h3>
                <p className="text-slate-500">{t('pricing_pos_subtitle')}</p>
              </div>

              {/* Monthly Price */}
              <div className="flex items-baseline gap-1">
                <span className="text-5xl font-bold text-[#22c55e]">€19.99</span>
                <span className="text-xl text-slate-500">{t('pricing_per_month')}</span>
              </div>

              {/* Yearly price with 15% off */}
              <div className="bg-slate-50 border-2 border-slate-200 px-4 py-3">
                <p className="text-sm font-semibold text-[#1e3a5f] mb-1">{t('pricing_yearly')} ({t('pricing_yearly_save')})</p>
                <span className="text-[#1e3a5f] font-bold text-lg">€{posYearly}{t('pricing_per_year')}</span>
                <span className="text-slate-400 text-sm ml-2">€{(posYearly / 12).toFixed(2)}{t('pricing_per_month')}</span>
              </div>

              {/* 50% off yearly offer */}
              <div className="bg-amber-50 border-2 border-amber-300 px-4 py-3">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                  <span className="text-amber-800 font-bold text-sm">50% OFF {t('pricing_yearly')}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="line-through text-slate-400 text-sm">€{posYearly}</span>
                  <span className="text-[#22c55e] font-bold text-xl">€{posOfferYearly}{t('pricing_per_year')}</span>
                </div>
                <p className="text-xs text-amber-700 mt-1">€{(posOfferYearly / 12).toFixed(2)}{t('pricing_per_month')} · {t('pricing_billed_yearly')}</p>
              </div>

              <ul className="space-y-3">
                {Array.isArray(posFeatures) && posFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#22c55e] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link to="/register?plan=pos"
                className="vf-btn-primary w-full flex items-center justify-center gap-2"
                data-testid="pricing-pos-button">
                {t('pricing_cta_pos')} <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
